import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import CreateLabelButton from '../../../components/grid-header/create-label-button';
import { CREATE_LABEL, CREATE_PRINT_LABEL, SHIPMENT_TYPES } from '../../../constants/create-label';
import { SHIPMENT, EXTERNAL } from '../../../constants/fulfillment-types';
import { createLookupHash } from '../../../services/hash-service';
import { purchaseBulkLabels } from '../../../store/orders/orders.actions';
import { buttonTextSettingName, getValidButtonIdForGrid } from '../helpers';
import { useInventoryAllocation } from '../../../hooks/inventory/use-inventory-allocation';
import Global from '../../../store/global-selectors';
import { createLabelSubject } from '../../../fulfillment-planner/context';
import { useContainsDemoOrder } from '../../../demos/hooks/use-contains-demo-order';

export const CreateLabelButtonContainer = ({ parentComponentName = '' }) => {
  const [isCreatingLabel, setIsCreatingLabel] = useState(false);
  const lastSuccessfulLabelCreationOrder = useRef();
  const hasExternalProviders = useSelector(Global.selectSellerActiveFulfillmentProviders).length > 0;
  const hasShipFrom = useSelector(Global.selectSellerShipFroms).length > 0 || hasExternalProviders;
  const bulkFlowStarted = useSelector(Global.selectBulkFlowStarted);
  const fulfillmentPlanId = useSelector(Global.selectSelectedOrdersIds)[0];
  const selectedOrders = useSelector(Global.selectSelectedOrdersIds);
  const fulfillmentPlans = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlansByIds(state, {
      fulfillmentPlanIds: selectedOrders,
    }),
  );
  const serviceIds = fulfillmentPlans.reduce((result, { serviceId }) => (serviceId ? [...result, serviceId] : result), []);
  const services = useSelector((state) => Global.selectSellerServicesByIds(state, { serviceIds })) || [];
  const servicesLookUpMap = createLookupHash(services, 'serviceId');
  const userCSWActionButtonId = useSelector((state) => Global.selectUserUserSetting(state, { userSetting: buttonTextSettingName }));
  const userHasShippingRole = useSelector(Global.selectUserhasShippingRole);
  const containsDemoOrder = useContainsDemoOrder(selectedOrders);

  const shipmentType = useMemo(() => {
    if (fulfillmentPlans.length) {
      const fulfillmentTypes = {
        [SHIPMENT]: SHIPMENT_TYPES.standard,
        [EXTERNAL]: SHIPMENT_TYPES.external,
      };

      const shipmentTypes = fulfillmentPlans.map(({ fulfillmentType, serviceId }) => {
        if (servicesLookUpMap[serviceId]?.features.supportsSameDayService) {
          return SHIPMENT_TYPES.local;
        }
        return fulfillmentTypes[fulfillmentType];
      });
      return shipmentTypes.every((st) => st === shipmentTypes[0]) ? shipmentTypes[0] : SHIPMENT_TYPES.multiple;
    }
  }, [fulfillmentPlans, servicesLookUpMap]);

  useEffect(() => {
    setIsCreatingLabel(false);
    const subscription = createLabelSubject.subscribe(
      ({ fulfillmentPlanId: createLabelFulfillmentPlanId, isCreatingLabel: creatingLabel, didCreateExternalFulfillment = false }) => {
        if (createLabelFulfillmentPlanId === fulfillmentPlanId) {
          if (didCreateExternalFulfillment) {
            lastSuccessfulLabelCreationOrder.current = createLabelFulfillmentPlanId;
          }
          setIsCreatingLabel(creatingLabel);
        }
      },
    );
    return () => subscription.unsubscribe();
  }, [fulfillmentPlanId]);

  const { allocateInventory, isAllocating } = useInventoryAllocation();
  const shouldBeDisabled =
    !selectedOrders.length ||
    bulkFlowStarted ||
    isCreatingLabel ||
    fulfillmentPlanId === lastSuccessfulLabelCreationOrder ||
    !userHasShippingRole ||
    isAllocating ||
    !hasShipFrom ||
    containsDemoOrder;

  const ordersGridActions = useMemo(() => {
    return {
      [CREATE_LABEL]: () => allocateInventory(selectedOrders, purchaseBulkLabels(false)),
      [CREATE_PRINT_LABEL]: () => allocateInventory(selectedOrders, purchaseBulkLabels(true)),
    };
  }, [allocateInventory, selectedOrders]);
  const handleOnClick = useCallback(
    ({ action = CREATE_LABEL }) => {
      ordersGridActions[action]();
    },
    [ordersGridActions],
  );

  const props = {
    actions: ordersGridActions,
    containsDemoOrder,
    primaryButtonPreference: getValidButtonIdForGrid(userCSWActionButtonId),
    selectedCount: selectedOrders.length,
    shipmentType,
    shouldBeDisabled,
    theme: 'primary',
    handleOnClick,
    selectedFulfillmentPlanIds: selectedOrders,
    parentComponentName,
  };

  return <CreateLabelButton {...props} />;
};

CreateLabelButtonContainer.propTypes = {
  parentComponentName: PropTypes.string,
};
